/**
 * QuestTracker -- Small HUD widget showing the currently tracked quest.
 *
 * Displays on the WorldTravel scene. Shows quest name, current objective,
 * and a directional indicator pointing toward the objective region.
 * Clicking opens the full quest log.
 *
 * @module QuestTracker
 */

import eventBus from '../../core/EventBus.js';
import questSystem from '../../systems/QuestSystem.js';
import travelSystem from '../../systems/TravelSystem.js';

export default class QuestTracker {
  /**
   * @param {object} [options]
   * @param {Function} [options.onOpenQuestLog] - Callback to open the quest log
   */
  constructor(options = {}) {
    this._onOpenQuestLog = options.onOpenQuestLog || null;

    /** @type {HTMLElement|null} Root element */
    this._el = null;

    /** @type {boolean} Whether the tracker is minimized */
    this._minimized = false;

    /** Event handler references */
    this._eventHandlers = {};
  }

  /**
   * Build and attach the quest tracker to the HUD.
   * @param {HTMLElement} parentEl - HUD element to attach to
   */
  attach(parentEl) {
    if (this._el) this.detach();

    this._el = document.createElement('div');
    this._el.className = 'quest-tracker';
    this._el.id = 'quest-tracker';

    // Click to open quest log
    this._el.addEventListener('click', () => {
      if (this._onOpenQuestLog) {
        this._onOpenQuestLog();
      }
    });

    parentEl.appendChild(this._el);

    this._subscribeEvents();
    this.refresh();
  }

  /**
   * Remove the tracker from the DOM.
   */
  detach() {
    this._unsubscribeEvents();

    if (this._el && this._el.parentNode) {
      this._el.parentNode.removeChild(this._el);
    }
    this._el = null;
  }

  /**
   * Toggle minimized state.
   */
  toggleMinimize() {
    this._minimized = !this._minimized;
    this.refresh();
  }

  /**
   * Refresh the tracker display.
   */
  refresh() {
    if (!this._el) return;

    const trackedId = questSystem.getTrackedQuestId();

    if (!trackedId) {
      this._el.innerHTML = '<div class="quest-tracker-empty">No tracked quest</div>';
      this._el.classList.add('quest-tracker-empty-state');
      return;
    }

    this._el.classList.remove('quest-tracker-empty-state');

    const state = questSystem.getQuestState(trackedId);
    if (!state) {
      this._el.innerHTML = '<div class="quest-tracker-empty">No tracked quest</div>';
      return;
    }

    const quest = state.quest;
    const step = quest.steps[state.currentStep];

    if (this._minimized) {
      this._el.innerHTML = '';
      this._el.classList.add('minimized');

      const minLabel = document.createElement('div');
      minLabel.className = 'quest-tracker-min-label';
      minLabel.textContent = quest.title;
      this._el.appendChild(minLabel);

      const expandBtn = document.createElement('button');
      expandBtn.className = 'quest-tracker-expand-btn';
      expandBtn.textContent = '+';
      expandBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        this.toggleMinimize();
      });
      this._el.appendChild(expandBtn);
      return;
    }

    this._el.classList.remove('minimized');
    this._el.innerHTML = '';

    // Header row
    const headerRow = document.createElement('div');
    headerRow.className = 'quest-tracker-header';

    // Type badge (small)
    const badge = document.createElement('span');
    badge.className = `quest-tracker-badge quest-badge-${quest.type}`;
    badge.textContent = quest.type === 'main' ? 'M' : 'S';
    headerRow.appendChild(badge);

    // Quest title
    const titleEl = document.createElement('span');
    titleEl.className = 'quest-tracker-title';
    titleEl.textContent = quest.title;
    headerRow.appendChild(titleEl);

    // Minimize button
    const minBtn = document.createElement('button');
    minBtn.className = 'quest-tracker-min-btn';
    minBtn.textContent = '-';
    minBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      this.toggleMinimize();
    });
    headerRow.appendChild(minBtn);

    this._el.appendChild(headerRow);

    // Current objective
    if (step) {
      const objEl = document.createElement('div');
      objEl.className = 'quest-tracker-objective';

      let objText = step.text;

      // Add kill progress if applicable
      if (step.type === 'kill' && step.targetCount) {
        const killCount = state.killProgress?.[step.targetEnemyId] || 0;
        objText += ` (${killCount}/${step.targetCount})`;
      }

      objEl.textContent = objText;
      this._el.appendChild(objEl);

      // Direction indicator
      if (step.targetRegionId) {
        const dirEl = document.createElement('div');
        dirEl.className = 'quest-tracker-direction';

        const currentRegion = travelSystem.currentRegionId;
        if (currentRegion === step.targetRegionId) {
          dirEl.textContent = '(Here)';
          dirEl.classList.add('quest-dir-here');
        } else {
          // Simple directional arrow based on region connections
          const arrow = this._getDirectionArrow(currentRegion, step.targetRegionId);
          dirEl.textContent = arrow;
          dirEl.classList.add('quest-dir-away');
        }

        this._el.appendChild(dirEl);
      }
    }

    // Step counter
    const stepCounter = document.createElement('div');
    stepCounter.className = 'quest-tracker-steps';
    stepCounter.textContent = `Step ${state.currentStep + 1} of ${quest.steps.length}`;
    this._el.appendChild(stepCounter);
  }

  /**
   * Get a directional arrow pointing toward a target region.
   * Uses a simple heuristic based on region connections.
   * @param {string} currentRegionId
   * @param {string} targetRegionId
   * @returns {string}
   */
  _getDirectionArrow(currentRegionId, targetRegionId) {
    // Simple BFS to find direction
    try {
      const dataStore = window.__debug?.dataStore;
      if (!dataStore) return '>>>';

      const currentRegion = dataStore.getById('regions', currentRegionId);
      if (!currentRegion || !currentRegion.connections) return '>>>';

      // Check if target is directly connected
      if (currentRegion.connections.includes(targetRegionId)) {
        return '>>>';
      }

      // Otherwise indicate it's further away
      return '>>> ...';
    } catch {
      return '>>>';
    }
  }

  // ── Events ────────────────────────────────────────────

  _subscribeEvents() {
    this._eventHandlers.onQuestProgress = () => this.refresh();
    this._eventHandlers.onQuestComplete = () => this.refresh();
    this._eventHandlers.onQuestStart = () => this.refresh();
    this._eventHandlers.onQuestAbandon = () => this.refresh();
    this._eventHandlers.onTrackedChanged = () => this.refresh();
    this._eventHandlers.onRegionChange = () => this.refresh();

    eventBus.on('quest:progress', this._eventHandlers.onQuestProgress);
    eventBus.on('quest:complete', this._eventHandlers.onQuestComplete);
    eventBus.on('quest:start', this._eventHandlers.onQuestStart);
    eventBus.on('quest:abandon', this._eventHandlers.onQuestAbandon);
    eventBus.on('quest:tracked-changed', this._eventHandlers.onTrackedChanged);
    eventBus.on('travel:region-change', this._eventHandlers.onRegionChange);
  }

  _unsubscribeEvents() {
    const events = [
      ['quest:progress', 'onQuestProgress'],
      ['quest:complete', 'onQuestComplete'],
      ['quest:start', 'onQuestStart'],
      ['quest:abandon', 'onQuestAbandon'],
      ['quest:tracked-changed', 'onTrackedChanged'],
      ['travel:region-change', 'onRegionChange'],
    ];

    for (const [event, key] of events) {
      if (this._eventHandlers[key]) {
        eventBus.off(event, this._eventHandlers[key]);
      }
    }
    this._eventHandlers = {};
  }
}
